/**
 * Lightweight error logging service.
 * Captures unhandled errors and logs them to the Supabase `error_logs` table.
 * In demo mode, errors are only printed to console.
 *
 * Features:
 * - Deduplication: same error message not logged more than once per minute
 * - Rate limiting: max 10 errors per minute to avoid flooding
 * - Non-blocking: fire-and-forget, never throws
 */

import { supabase, isDemoMode } from '@/lib/supabase';

// --- Rate limiting & dedup state ---

const DEDUP_WINDOW_MS = 60_000; // 1 minute
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 60_000;

const recentErrors = new Map<string, number>(); // message -> timestamp
let rateLimitCounter = 0;
let rateLimitWindowStart = Date.now();

function isDuplicate(message: string): boolean {
  const now = Date.now();
  const lastSeen = recentErrors.get(message);
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return true;
  }
  recentErrors.set(message, now);
  // Cleanup old entries periodically
  if (recentErrors.size > 100) {
    for (const [key, ts] of recentErrors) {
      if (now - ts > DEDUP_WINDOW_MS) recentErrors.delete(key);
    }
  }
  return false;
}

function isRateLimited(): boolean {
  const now = Date.now();
  if (now - rateLimitWindowStart > RATE_LIMIT_WINDOW_MS) {
    rateLimitCounter = 0;
    rateLimitWindowStart = now;
  }
  if (rateLimitCounter >= RATE_LIMIT_MAX) {
    return true;
  }
  rateLimitCounter++;
  return false;
}

// --- Cached user info (resolved lazily) ---

let cachedUserId: string | null = null;
let cachedCenterId: string | null = null;
let userInfoResolved = false;

async function resolveUserInfo(): Promise<void> {
  if (userInfoResolved) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      cachedUserId = session.user.id;
      // Try to get center_id from profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('center_id')
        .eq('id', session.user.id)
        .single();
      if (profile?.center_id) {
        cachedCenterId = profile.center_id;
      }
    }
    userInfoResolved = true;
  } catch {
    // Silently fail -- user info is best-effort
  }
}

// Listen for auth changes to update cached info
supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.user) {
    cachedUserId = session.user.id;
    userInfoResolved = false; // re-resolve center_id on next error
  } else {
    cachedUserId = null;
    cachedCenterId = null;
    userInfoResolved = false;
  }
});

// --- Core logger ---

export interface ErrorContext {
  component?: string;
  action?: string;
  extra?: Record<string, unknown>;
}

/**
 * Log an error to the Supabase error_logs table.
 * Non-blocking, fire-and-forget. Never throws.
 */
export async function logError(error: unknown, context?: ErrorContext): Promise<void> {
  try {
    const message = extractMessage(error);
    const stack = extractStack(error);

    // Console log always (useful for dev)
    console.error('[ErrorLogger]', message, context || '');

    if (isDemoMode) return;
    if (isDuplicate(message)) return;
    if (isRateLimited()) return;

    // Resolve user info (best-effort, non-blocking)
    await resolveUserInfo();

    const payload = {
      message: message.slice(0, 2000), // cap message length
      stack: stack ? stack.slice(0, 8000) : null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      user_id: cachedUserId || null,
      center_id: cachedCenterId || null,
      context: context ? JSON.parse(JSON.stringify(context)) : null,
    };

    // Fire and forget
    supabase.from('error_logs').insert(payload).then(({ error: insertErr }) => {
      if (insertErr) {
        console.warn('[ErrorLogger] Failed to persist error log:', insertErr.message);
      }
    });
  } catch {
    // The error logger must never throw
  }
}

function extractMessage(error: unknown): string {
  if (error === null || error === undefined) return 'Unknown error (empty rejection reason)';
  if (error instanceof Error) return error.message || error.name || 'Empty Error';
  if (typeof error === 'string') return error;
  if (typeof error === 'number' || typeof error === 'boolean') return String(error);
  if (typeof error === 'object') {
    const e = error as Record<string, unknown>;
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.reason === 'string' && e.reason) return e.reason;
    if (typeof e.error === 'string' && e.error) return e.error;
    // Try a JSON dump so we can at least see the shape
    try {
      const json = JSON.stringify(error);
      if (json && json !== '{}' && json !== 'null') {
        return `Object rejection: ${json.slice(0, 500)}`;
      }
    } catch { /* non-serializable */ }
    const ctor = (error as object).constructor?.name;
    if (ctor && ctor !== 'Object') return `Rejection of type ${ctor}`;
  }
  return String(error);
}

function extractStack(error: unknown): string | null {
  if (error instanceof Error && error.stack) return error.stack;
  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>;
    if (typeof e.stack === 'string') return e.stack;
  }
  return null;
}

// --- Global error handlers ---

/**
 * Install global error handlers (window.onerror, unhandledrejection).
 * Call once at app startup (e.g. in main.tsx).
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    logError(event.error || event.message, {
      component: 'window.onerror',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (isIgnorableRejection(reason)) return;

    // Capture diagnostic metadata so future "Rejected" entries are actionable
    const reasonType = typeof reason;
    const reasonCtor = reason && typeof reason === 'object'
      ? (reason as object).constructor?.name || null
      : null;
    let reasonJson: string | null = null;
    if (reason && typeof reason === 'object' && !(reason instanceof Error)) {
      try { reasonJson = JSON.stringify(reason).slice(0, 500); } catch { /* non-serializable */ }
    }
    // Synthetic stack from the handler — won't show the original Promise origin
    // but at least confirms the path through our handler
    const syntheticStack = !(reason instanceof Error) ? new Error('synthetic').stack : undefined;

    logError(reason, {
      component: 'unhandledrejection',
      extra: { reasonType, reasonCtor, reasonJson, syntheticStack },
    });
  });
}

/**
 * Filter rejections that are known noise (AbortError on navigation, browser
 * extension errors, ResizeObserver internals, supabase-realtime "Rejected"
 * string). These pollute the daily report without being actionable.
 */
function isIgnorableRejection(reason: unknown): boolean {
  if (reason instanceof DOMException && reason.name === 'AbortError') return true;
  if (typeof reason === 'string') {
    if (reason === 'Rejected') return true; // supabase-realtime channel join failure noise
    if (/^ResizeObserver loop/i.test(reason)) return true;
  }
  if (reason && typeof reason === 'object') {
    const msg = (reason as { message?: unknown }).message;
    if (typeof msg === 'string') {
      if (/^ResizeObserver loop/i.test(msg)) return true;
      if (/^The user aborted a request/i.test(msg)) return true;
    }
  }
  return false;
}
